import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, BarChart3, Cpu, Search, TrendingUp, Handshake, Leaf, MapPin, Check } from "lucide-react";
import { Reveal } from "../components/Motion";
import { IMAGES, PILLARS, STATS, SERVICES, PROJECTS, REGIONS } from "../data";

const ICONS = { BarChart3, Cpu, Search, TrendingUp, Handshake, Leaf };

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[94vh] flex items-center overflow-hidden bg-mm-navy">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-24">
          <img src={IMAGES.pitAerial} alt="Open-pit mining operation" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-mm-navy via-mm-navy/85 to-mm-navy/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-mm-navy/85 via-transparent to-mm-navy/20" />

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-24 pb-28">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="flex items-center gap-3 text-mm-accent font-mono text-xs tracking-[0.24em] uppercase mb-7"
          >
            <span className="h-px w-10 bg-mm-accent" /> Mars Mining Limited · London
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.1 }}
            className="font-display font-extrabold text-white text-4xl sm:text-6xl lg:text-[4.6rem] leading-[1.02] tracking-tight max-w-4xl"
          >
            Mining the future <span className="text-mm-accent">with intelligence.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.25 }}
            className="mt-7 max-w-2xl text-lg md:text-xl text-white/80 leading-relaxed"
          >
            A UK-based mining intelligence and strategic project platform — combining AI, geoscience and international partnerships to secure access to the critical minerals shaping the energy transition.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/projects" data-testid="hero-projects-btn" className="inline-flex items-center gap-2 px-7 py-3.5 bg-mm-accent text-white font-semibold rounded-md hover:bg-white hover:text-mm-navy transition-colors">
              Explore Projects <ArrowRight size={17} />
            </Link>
            <Link to="/contact" data-testid="hero-contact-btn" className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/40 text-white font-semibold rounded-md hover:bg-white/10 transition-colors">
              Contact Our Team
            </Link>
          </motion.div>
        </div>

        {/* Pillars bar */}
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-mm-navy/40 backdrop-blur-md">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex flex-wrap items-center justify-center md:justify-between gap-x-8 gap-y-3">
            {PILLARS.map((p, i) => {
              const c = ["text-brand-orange", "text-brand-teal", "text-brand-red", "text-brand-green"][i % 4];
              return (
                <span key={p} className="flex items-center gap-2 font-mono text-[0.7rem] tracking-[0.14em] uppercase text-white/85">
                  <Check size={14} className={c} /> {p}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT INTRO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal>
            <p className="overline mb-5">Who we are</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-mm-ink">
              Mineral intelligence meets international investment.
            </h2>
            <p className="mt-6 text-lg text-mm-muted leading-relaxed">
              Mars Mining Limited combines artificial intelligence, geoscience, project development expertise and strategic partnerships to enable secure, sustainable access to critical minerals.
            </p>
            <p className="mt-4 text-lg text-mm-muted leading-relaxed">
              We support governments, mining companies and investors across the value chain — identifying, evaluating and advancing high-potential opportunities while reducing exploration and investment risk.
            </p>
            <Link to="/about" data-testid="home-about-link" className="inline-flex items-center gap-2 mt-8 font-semibold text-mm-blue hover:text-mm-accent transition-colors">
              More about Mars Mining <ArrowRight size={17} />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="relative aspect-[4/3] overflow-hidden rounded-lg shadow-card">
              <img src={IMAGES.engineers} alt="Mining engineers on site" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-mm-navy text-white rounded-lg px-7 py-5 shadow-cardHover">
              <p className="font-display font-extrabold text-3xl">Responsible</p>
              <p className="font-mono text-xs tracking-widest uppercase text-mm-accent mt-1">Development First</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="bg-mm-navy">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="py-14 px-6 first:pl-0">
              <p className="font-display font-extrabold text-5xl md:text-6xl text-white">{s.value}</p>
              <p className="mt-3 font-mono text-xs tracking-[0.16em] uppercase text-mm-accent">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-2xl mb-14">
          <Reveal>
            <p className="overline mb-5">What we do</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-mm-ink">
              End-to-end expertise across the resource lifecycle.
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`service-${i}`} className="group h-full bg-white border border-mm-line rounded-lg p-7 shadow-card hover:shadow-cardHover hover:-translate-y-1 transition-all duration-300">
                  <div className="h-12 w-12 rounded-md bg-mm-light group-hover:bg-mm-accent flex items-center justify-center transition-colors">
                    {Icon && <Icon size={22} className="text-mm-blue group-hover:text-white transition-colors" />}
                  </div>
                  <h3 className="font-display font-bold text-xl text-mm-ink mt-6">{s.title}</h3>
                  <p className="text-mm-muted leading-relaxed mt-3">{s.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="bg-mm-light border-y border-mm-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-12">
            <div>
              <p className="overline mb-5">Portfolio</p>
              <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight text-mm-ink">Selected projects</h2>
            </div>
            <Link to="/projects" data-testid="home-projects-link" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-mm-line rounded-md font-semibold text-mm-blue hover:border-mm-accent transition-colors">
              View full portfolio <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to="/projects" data-testid={`home-project-${p.id}`} className="group block bg-white border border-mm-line rounded-lg overflow-hidden shadow-card hover:shadow-cardHover transition-all duration-300">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-white/95 rounded font-mono text-[0.6rem] tracking-widest uppercase text-mm-blue">{p.stage}</span>
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-xs tracking-[0.14em] uppercase text-mm-accent">{p.mineral}</p>
                    <h3 className="font-display font-bold text-xl text-mm-ink mt-2">{p.name}</h3>
                    <p className="flex items-center gap-1.5 text-sm text-mm-muted mt-1"><MapPin size={14} /> {p.location} · {p.region}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL REACH */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="aspect-[4/3] overflow-hidden rounded-lg shadow-card">
              <img src={IMAGES.london} alt="London" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <p className="overline mb-5">Global reach</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-mm-ink">
              London-headquartered, spanning four continents.
            </h2>
            <div className="mt-8 divide-y divide-mm-line border-t border-mm-line">
              {REGIONS.map((r) => (
                <div key={r.name} className="flex items-baseline justify-between gap-4 py-4">
                  <span className="font-display font-bold text-lg text-mm-ink w-32 shrink-0">{r.name}</span>
                  <span className="text-mm-muted flex-1">{r.role}</span>
                  <span className="hidden sm:block font-mono text-xs text-mm-steel">{r.note}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-mm-navy">
        <img src={IMAGES.excavators} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl text-white leading-tight tracking-tight max-w-3xl mx-auto">
              Connect your capital with the minerals shaping the next century.
            </h2>
            <Link to="/contact" data-testid="home-cta-contact" className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-mm-accent text-white font-semibold rounded-md hover:bg-white hover:text-mm-navy transition-colors">
              Start a conversation <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
